import { ChangeDetectionStrategy, Component, inject, OnInit, ViewChild, ElementRef, signal } from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { MatDialogModule, MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';
import { FormArray, FormBuilder, FormGroup, FormsModule, ReactiveFormsModule, Validators } from '@angular/forms';
import { ConsumerJsonModel, Theme } from '../consumer.json.model';
import { NoAuthorization } from '../noAuthorization.model';
import { BasicAuth } from '../basicAuth.model';
import { urlValidator } from '../../core/util';
import { MatDividerModule } from '@angular/material/divider';
import { MatTabsModule } from '@angular/material/tabs';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { Store } from '@ngxs/store';
import { AppSelectors } from '../../app.state';
import { SubjectJsonModel } from '../../subject/subject.json.model';
import { defaultTheme, fonts } from '../../core/types';
import { NgxColorsModule } from 'ngx-colors';
import { DomSanitizer, SafeResourceUrl } from '@angular/platform-browser';
import { MatTooltipModule } from '@angular/material/tooltip';
import { PlayerMessageService } from '../../core/services/player.service';
import { TranslocoModule } from '@jsverse/transloco';

// Interface für gruppierte Kompetenzen
interface GroupedCompetencies {
  letter: string
  competencies: SubjectJsonModel[]
}

@Component({
  selector: 'pixl-edit-consumer',
  imports: [
    MatDialogModule,
    MatButtonModule,
    MatFormFieldModule,
    MatInputModule,
    MatIconModule,
    FormsModule,
    ReactiveFormsModule,
    MatDividerModule,
    MatTabsModule,
    MatCheckboxModule,
    MatSelectModule,
    MatTooltipModule,
    NgxColorsModule,
    TranslocoModule,
  ],
  providers: [
    NgxColorsModule
  ],
  templateUrl: './edit-consumer.component.html',
  styleUrl: './edit-consumer.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class ConsumerEditComponent implements OnInit {
  @ViewChild('playerPreviewIframe') playerPreviewIframe!: ElementRef<HTMLIFrameElement>;

  private fb = inject(FormBuilder);
  private readonly store = inject(Store);
  private sanitizer = inject(DomSanitizer);
  private resetTheme!: Theme;
  private appState$ = this.store.select(AppSelectors.appState);
  private playerService = inject(PlayerMessageService);
  private dialogRef = inject(MatDialogRef<ConsumerEditComponent, ConsumerJsonModel>);

  public consumerForm!: FormGroup;
  public competencies: SubjectJsonModel[] = [];
  public availableFonts = fonts;
  public groupedCompetencies: GroupedCompetencies[] = [];
  public disabledColorSchemeBtn = signal(true);
  public data = inject<ConsumerJsonModel>(MAT_DIALOG_DATA);

  private consumerAuth = ((this.data.authorization as NoAuthorization | BasicAuth == null) ? new NoAuthorization() : this.data.authorization);
  private selectedAuth = this.consumerAuth._t;

  public iframePreviewUrl: SafeResourceUrl | null = null;

  constructor() {
    this.initForm();
  }

  ngOnInit(): void {
    this.appState$.subscribe(({ isLoaded, subjects }) => {
      if (isLoaded) {
        this.competencies = subjects;
        this.initSubjectConfigs();
        this.groupCompetenciesByFirstLetter();
      }
    });

    if (this.data.hasId()) {
      this.iframePreviewUrl = this.sanitizer.bypassSecurityTrustResourceUrl(this.playerService.theme2PlayerPreviewUrl(this.data.id));
    }
  }

  private initSubjectConfigs(): void {
    const subjectConfigsFArray = this.consumerForm.get('playerSettings.subjectConfigs') as FormArray;
    subjectConfigsFArray.clear();

    const initialSubjectConfigs = this.data.playerSettings?.subjectConfigs || [];

    const rootCompetencies = this.competencies.filter(competency => competency.root === true);

    rootCompetencies.forEach(competency => {
      const subjectIdToUse = (competency.treeId && competency.treeId.trim() !== '') ? competency.treeId : '0';

      const existingConfig = initialSubjectConfigs.find(
        config => config.subjectId === subjectIdToUse
      );

      const fontId = existingConfig?.fontId || 'def';

      subjectConfigsFArray.push(this.fb.group({
        subjectId: [subjectIdToUse, Validators.required],
        fontId: [fontId],
      }));
    });
  }

  private initForm(): void {
    const nlpSettings = this.data.nlpSettings || {};
    const playerSettings = this.data.playerSettings || {};

    this.consumerForm = this.fb.group({
      name: [this.data.name, [Validators.required]],
      description: [this.data.description, [Validators.required]],
      kiService: this.data.kiService,
      baseColor: this.data.baseColor,
      playerFilesFolder: this.data.playerFilesFolder,
      url: [this.data.url, [Validators.required, urlValidator]],
      version: this.data.version,
      authorization: this.selectedAuth,
      availableClasses: this.fb.array([]),
      nlpSettings: this.fb.group({
        nlpFeedbackLanguage: nlpSettings.nlpFeedbackLanguage || 'de',
        nlpFeedbackCorrectnessThreshold: nlpSettings.nlpFeedbackCorrectnessThreshold,
      }),
      playerSettings: this.fb.group({
        selfJudgementPopup: playerSettings.selfJudgementPopup,
        dontKnowBtn: playerSettings.dontKnowBtn,
        readoutExerciseText: playerSettings.readoutExerciseText,
        randomOrderOfTestSequences: playerSettings.randomOrderOfTestSequences,
        textSelectionEnabled: playerSettings.textSelectionEnabled,
        emptyAnswerWarningPopup: playerSettings.emptyAnswerWarningPopup,
        checkAnswerPopup: playerSettings.checkAnswerPopup,
        showNoteButton: playerSettings.showNoteButton,
        showBottomPart: playerSettings.showBottomPart,
        showSelfRating: playerSettings.showSelfRating,
        showExerciseSequenceTabs: playerSettings.showExerciseSequenceTabs,
        hideExerciseHeadline: playerSettings.hideExerciseHeadline,
        maxTries: playerSettings.maxTries?.toString(),
        retryMode: playerSettings.retryMode,
        hideResetExerciseButton: playerSettings.hideResetExerciseButton,
        showCompletionIcons: playerSettings.showCompletionIcons,
        tabLetters: playerSettings.tabLetters,
        disableExerciseFeedback: playerSettings.disableExerciseFeedback,
        flattenExercises: playerSettings.flattenExercises,
        footerInfo: playerSettings.footerInfo,
        theme: this.fb.group({
          primaryColor: playerSettings.theme?.primaryColor || defaultTheme.primaryColor,
          secondaryColor: playerSettings.theme?.secondaryColor || defaultTheme.secondaryColor,
          tertiaryColor: playerSettings.theme?.tertiaryColor || defaultTheme.tertiaryColor,
          placeholderColor: playerSettings.theme?.placeholderColor || defaultTheme.placeholderColor,
          hoverColor: playerSettings.theme?.hoverColor || defaultTheme.hoverColor,
          drawColor: playerSettings.theme?.drawColor || defaultTheme.drawColor,
          drawHighlightColor: playerSettings.theme?.drawHighlightColor || defaultTheme.drawHighlightColor,
          compareSolutionsBgColor: playerSettings.theme?.compareSolutionsBgColor || defaultTheme.compareSolutionsBgColor,
        }),
        allowedFrontendOrigins: this.fb.array([]),
        subjectConfigs: this.fb.array([]),
      }),
      consumerMetadata: this.fb.array([]),
      learnObjectTypeDescriptions: this.fb.array([]),
    });

    this.createResetTheme();
    this.initSubjectConfigs();
  }

  private createResetTheme(): void {
    const playerSettingsTheme = this.data.playerSettings?.theme;

    this.resetTheme = Object.assign(
      {},
      defaultTheme,
      playerSettingsTheme
    );
  }

  private groupCompetenciesByFirstLetter(): void {
    const grouped: Record<string, SubjectJsonModel[]> = {};

    const sortableCompetencies = [...this.competencies];

    sortableCompetencies.sort((a, b) => a.name.localeCompare(b.name));

    sortableCompetencies.forEach(comp => {
      const firstLetter = comp.name ? comp.name.charAt(0).toUpperCase() : '#';
      if (!grouped[firstLetter]) {
        grouped[firstLetter] = [];
      }
      grouped[firstLetter].push(comp);
    });

    this.groupedCompetencies = Object.keys(grouped)
      .sort()
      .map(letter => ({
        letter: letter,
        competencies: grouped[letter]
      }));
  }

  public onSave(): void {
    if (!this.consumerForm.valid) {
      return this.consumerForm.markAllAsTouched();
    }

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    (this.data as any).update(this.consumerForm.value);
    if (this.consumerForm.value.authorization == 'BasicAuth') {
      this.data.authorization = new BasicAuth();
    }
    else {
      this.data.authorization = new NoAuthorization();
    }

    this.dialogRef.close(this.data);
  }

  public onCancel(): void {
    this.dialogRef.close();
  }

  public getFont(competency: SubjectJsonModel): string {
    const subjectConfigsFormArrayValue = this.consumerForm.get('playerSettings.subjectConfigs')?.value;

    if (Array.isArray(subjectConfigsFormArrayValue)) {
      for (const config of subjectConfigsFormArrayValue) {
        if (config.subjectId === competency.treeId) {
          return config.fontId;
        }
      }
    }
    return 'def';
  }

  public setDefaultTheme(): void {
    const themeFormGroup = this.consumerForm.get('playerSettings.theme') as FormGroup;
    if (themeFormGroup) {
      themeFormGroup.patchValue(this.resetTheme);
      this.sendThemeToPlayer();
    }
    this.disabledColorSchemeBtn.set(true);
  }

  public setFont(event: string, competency: SubjectJsonModel): void {
    const newFontId = event || 'def';

    const subjectConfigsFormArray = this.consumerForm.get('playerSettings.subjectConfigs') as FormArray;

    const configIndex = subjectConfigsFormArray.controls.findIndex(control => {
      const configGroup = control as FormGroup;
      return configGroup.get('subjectId')?.value === competency.treeId;
    });

    if (configIndex !== -1) {
      const configGroup = subjectConfigsFormArray.at(configIndex) as FormGroup;
      configGroup.get('fontId')?.setValue(newFontId);
    }
    else {
      console.warn(`No matching subjectConfig found for competency: ${competency.treeId}`);
    }
  }

  public get subjectConfigs(): FormArray {
    return (this.consumerForm.get('playerSettings') as FormGroup).get('subjectConfigs') as FormArray;
  }

  public get subjectConfigGroups(): FormGroup[] {
    return this.subjectConfigs.controls as FormGroup[];
  }

  public onIframeLoaded(): void {
    this.playerService.iFrameReady = true;
    this.playerService.playerPreviewIFrame = this.playerPreviewIframe;

    this.playerService.postMessageToPlayer(this.playerService.theme2PlayerPreviewUrl(this.data.id), { themePreview: 'redaInitIframe' });
    this.sendThemeToPlayer();
  }

  public sendThemeToPlayer() {
    if (this.playerService.iFrameReady) {
      const themeColors = this.consumerForm.get('playerSettings.theme')?.value;

      if (themeColors) {
        this.disabledColorSchemeBtn.set(false);

        const message = {
          themePreview: {
            themeColors: {
              compareSolutionsBgColor: themeColors.compareSolutionsBgColor,
              drawColor: themeColors.drawColor,
              drawHighlightColor: themeColors.drawHighlightColor,
              hoverColor: themeColors.hoverColor,
              placeholderColor: themeColors.placeholderColor,
              primaryColor: themeColors.primaryColor,
              secondaryColor: themeColors.secondaryColor,
              tertiaryColor: themeColors.tertiaryColor,
            }
          }
        };

        this.playerService.postMessageToPlayer(this.playerService.theme2PlayerPreviewUrl(this.data.id), message);
      }
    }
  }
}
